import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../networking/client';
import { RootState } from '../store';

export interface IServiceInfo {
  service_execution_category_id?: number;
  service_execution_category?: string;
  external_account_number?: string;
  location_external_id?: string;
}

export interface IServiceInfoByLocation {
  service_execution_category_id?: number;
  service_execution_category?: string;
  external_account_number?: string;
  location_external_id?: string;
  location_id?: number;
  business_partner_name1?: string;
  service_id?: number;
  product_id?: number;
}

export interface IServiceWithBiomarkerInfo {
  biomarker_id: number;
  service_id: number;
  value1: number;
  value2: number;
}

export interface IServicesState {
  status: string;
  serviceInfos: IServiceInfo[];
  servicesByLocation: IServiceInfoByLocation[];
  fetchStatus: string;
  servicesWithBiomarkerInfo: IServiceWithBiomarkerInfo[];
}

const initialState: IServicesState = {
  status: 'idle',
  serviceInfos: [],
  servicesByLocation: [],
  fetchStatus: 'idle',
  servicesWithBiomarkerInfo: [],
};

export const fetchServices = createAsyncThunk(
  'servicesById/fetchAll',
  async (serviceId: string, { dispatch, rejectWithValue }) => {
    try {
      const getServices: any = await client.get(`/healthpoint/service-info/${serviceId}`);
      return getServices;
    } catch (err: any) {
      return rejectWithValue({});
    }
  }
);

export const fetchServicesForLocation = createAsyncThunk(
  'servicesByLocationId/fetchAll',
  async (locationId: string, { dispatch, rejectWithValue }) => {
    try {
      const getServices: any = await client.get(`/healthpoint/all-service-info/${locationId}`);
      return getServices;
    } catch (err: any) {
      return rejectWithValue({});
    }
  }
);

export const fetchServicesWithBiomarkerInfo = createAsyncThunk(
  'servicesWithBiomarkerInfo/fetch',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response: any = await client.get(`/healthpoint/service-reports`);
      return response;
    } catch (err: any) {
      return rejectWithValue({});
    }
  }
);

export const servicesSlice = createSlice({
  name: 'services',
  initialState,
  reducers: {
    resetServices: (_) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.serviceInfos = action.payload.data.services;
        state.status = 'fulfilled';
      })
      .addCase(fetchServices.rejected, (state) => {
        state.status = 'rejected';
      })
      .addCase(fetchServicesForLocation.pending, (state) => {})
      .addCase(fetchServicesForLocation.fulfilled, (state, action) => {
        state.servicesByLocation = action.payload.data.services;
      })
      .addCase(fetchServicesForLocation.rejected, (state) => {})
      .addCase(fetchServicesWithBiomarkerInfo.pending, (state) => {
        state.fetchStatus = 'pending';
      })
      .addCase(fetchServicesWithBiomarkerInfo.fulfilled, (state, action) => {
        state.servicesWithBiomarkerInfo = action.payload.data.serviceReports;
        state.fetchStatus = 'fulfilled';
      })
      .addCase(fetchServicesWithBiomarkerInfo.rejected, (state) => {
        state.fetchStatus = 'rejected';
      });
  },
});

export const { resetServices } = servicesSlice.actions;

export default servicesSlice.reducer;

export const selectServiceInfos = (state: RootState) => state.services.serviceInfos;
export const selectServiceInfosForLocation = (state: RootState) =>
  state.services.servicesByLocation;
export const selectServicesFetchStatus = (state: RootState) => state.services.status;
export const selectServicesWithBiomarkerInfos = (state: RootState) =>
  state.services.servicesWithBiomarkerInfo;
